import React, { useEffect } from 'react';
import { CategoryType, QuickActionTemplate } from './types';
import { CategoryTabs } from './CategoryTabs';
import { ActionCard } from './ActionCard';
import styles from './QuickActionGrid.module.css';
import useQuickActionsStore, { CardType } from '../../store/quickActionsStore';
import { trackEvent } from '../../services/analyticsService';
import { logDebug } from '../../utils/logging';

const CATEGORIES: CategoryType[] = ['Toate', 'Copywriting', 'Document', 'Altele'];

interface QuickActionGridProps {
  cardType: CardType;
  onTemplateSelect: (template: QuickActionTemplate) => void;
}

export const QuickActionGrid: React.FC<QuickActionGridProps> = ({ 
  cardType,
  onTemplateSelect 
}) => {
  const {
    filteredTemplates,
    selectedCategory,
    isLoading,
    error,
    loadTemplates,
    setSelectedCategory,
    setSelectedCardType,
  } = useQuickActionsStore();

  // Load templates when component mounts or cardType changes
  useEffect(() => {
    logDebug('[QuickActionGrid] Loading templates for cardType:', cardType);
    setSelectedCardType(cardType);
    loadTemplates(cardType);
  }, [cardType, loadTemplates, setSelectedCardType]);

  const handleCategoryChange = (category: CategoryType) => {
    logDebug('[QuickActionGrid] Category changed:', category);
    setSelectedCategory(category);
    
    // Track category selection
    trackEvent('quick_action_category_changed', {
      category,
      card_type: cardType,
      template_count: filteredTemplates.length
    });
  };

  const handleTemplateSelect = (template: QuickActionTemplate) => {
    logDebug('[QuickActionGrid] Template selected:', template.template_id);
    
    // Track template selection
    trackEvent('quick_action_template_selected', {
      template_id: template.template_id,
      category: template.category,
      card_type: template.card_type
    });
    
    onTemplateSelect(template);
  };

  if (error) {
    return (
      <div className={styles.errorState}>
        <p className={styles.errorMessage}>
          Ne pare rău, a apărut o eroare la încărcarea opțiunilor.
        </p>
        <button 
          className={styles.retryButton}
          onClick={() => {
            // Retry loading templates
            loadTemplates(cardType);
          }}
        >
          Încearcă din nou
        </button>
      </div>
    );
  }

  return (
    <div className={styles.quickActionGrid}>
      <CategoryTabs
        categories={CATEGORIES}
        selectedCategory={selectedCategory}
        onCategoryChange={handleCategoryChange}
      />
      
      <div className={styles.templateGrid} role="list" aria-label="Lista șabloanelor disponibile">
        {isLoading ? (
          // Loading skeleton - will be enhanced in Task F1.4
          <div className={styles.loadingState}>
            <p>Se încarcă opțiunile...</p>
          </div>
        ) : (
          filteredTemplates.map((template) => (
            <ActionCard
              key={template.template_id}
              template={template}
              onClick={() => handleTemplateSelect(template)}
            />
          ))
        )}
      </div>
    </div>
  );
}; 